import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API, errorText } from '../governance_v2/constants';
import { RISK_OPTIONS } from './constants';
import { SimilarDecisions } from '../memory/SimilarDecisions';

const NUM = ['expected_value', 'impact_pct', 'confidence'];
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v != null)
  .map(([k, v]) => [k, NUM.includes(k) ? Number(v) : v]));

const Field = ({ id, label, children }) => <div className="space-y-1"><Label htmlFor={id} className="text-xs">{label}</Label>{children}</div>;

export const ProposalFromSignalDialog = ({ companyId, signal, onClose }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  useEffect(() => { if (signal) setForm({ ...signal.proposal_draft }); }, [signal]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: async () => (await axios.post(`${API}/companies/${companyId}/materiality/signals/${signal.id}/proposal`, clean(form))).data,
    onSuccess: (d) => {
      toast.success(d.proposal.status === 'pending' ? 'Propuesta creada: requiere aprobación humana' : 'Propuesta creada');
      qc.invalidateQueries({ queryKey: ['materiality'] });
      onClose();
    },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Dialog open={!!signal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="mat-proposal-dialog">
        <DialogHeader><DialogTitle>Crear propuesta gobernada</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Prellenada con las cifras del motor de materialidad. Pasa por las reglas institucionales y, si aplica, por aprobación humana.</p>
        <div className="grid gap-3">
          <Field id="mp-title" label="Título"><Input id="mp-title" value={form.title || ''} onChange={set('title')} data-testid="mat-proposal-title" /></Field>
          <Field id="mp-desc" label="Descripción"><Textarea id="mp-desc" rows={4} value={form.description || ''} onChange={set('description')} data-testid="mat-proposal-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field id="mp-risk" label="Riesgo">
              <Select value={form.risk_level || ''} onValueChange={(v) => setForm((f) => ({ ...f, risk_level: v }))}>
                <SelectTrigger id="mp-risk" data-testid="mat-proposal-risk"><SelectValue placeholder="Riesgo" /></SelectTrigger>
                <SelectContent>{RISK_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field id="mp-impact" label="Impacto %"><Input id="mp-impact" type="number" value={form.impact_pct ?? ''} onChange={set('impact_pct')} data-testid="mat-proposal-impact" /></Field>
            <Field id="mp-kpi" label="KPI"><Input id="mp-kpi" value={form.kpi_name || ''} onChange={set('kpi_name')} data-testid="mat-proposal-kpi" /></Field>
            <Field id="mp-exp" label="Valor esperado"><Input id="mp-exp" type="number" value={form.expected_value ?? ''} onChange={set('expected_value')} data-testid="mat-proposal-expected" /></Field>
            <Field id="mp-date" label="Fecha esperada"><Input id="mp-date" type="date" value={form.expected_date || ''} onChange={set('expected_date')} data-testid="mat-proposal-date" /></Field>
          </div>
        </div>
        {signal && <SimilarDecisions companyId={companyId} moduleType={signal.module_type} kpiName={form.kpi_name} text={form.title} testid="mat-similar" />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="mat-proposal-cancel">Cancelar</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || (form.title || '').trim().length < 3} data-testid="mat-proposal-submit">
            {create.isPending ? 'Creando…' : 'Crear propuesta'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
